import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { isPublicRoute } from '../../lib/publicRoutes';
import { CHAT_PHASE_ATTR } from '../../lib/navigation/chatPhase';
import { isAskPraxisFabVisible, isAnalyticsAskEligible } from './askPraxisFabVisibility';
import { useFabStackSlot } from '../../contexts/FabStackContext';
import { FAB_PRIORITY } from '../../lib/fabStack';
import { isFabAllowedOnMobile, FAB_MOBILE_BREAKPOINT_QUERY } from '../../lib/fabMobileVisibility';
import { mediaQueryMatches, safeMatchMedia, subscribeMediaQuery } from '../../lib/safeMatchMedia';
import AnalyticsAsk from './AnalyticsAsk.jsx';
import './analyticsAskFab.css';

const ASK_PRAXIS_ICON = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export default function AnalyticsAskFab() {
  const { isAuthenticated, user } = useAuth();
  const { appName } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [chatPhase, setChatPhase] = useState(null);

  // #2079: mobile viewport tracking, mirroring VoiceChat.jsx's existing
  // `matchMedia('(max-width: 767px)')` pattern (same breakpoint via the
  // shared FAB_MOBILE_BREAKPOINT_QUERY constant so it can't drift from the
  // CSS tokens' <=767px block in App.css). Eager init avoids a
  // desktop-to-mobile flash on first paint (safe in this Vite SPA, no SSR).
  // Guarded (typeof window / matchMedia), unlike VoiceChat.jsx's copy of this
  // pattern, because this component IS unit-rendered without jsdom via
  // renderToStaticMarkup in AnalyticsAskFab.test.jsx (project convention —
  // see VoiceCaptureIndicator.jsx / ThemeContext.jsx for the same guard).
  // #4215: both reads go through the throw-safe helper (fallback false).
  const [isMobile, setIsMobile] = useState(() => mediaQueryMatches(FAB_MOBILE_BREAKPOINT_QUERY, false));
  useEffect(() => {
    const mql = safeMatchMedia(FAB_MOBILE_BREAKPOINT_QUERY);
    if (!mql) return undefined;
    return subscribeMediaQuery(mql, (e) => setIsMobile(e.matches));
  }, []);

  // Subscribe to ChatPage's published phase (#1555 bridge) so the FAB hides for the
  // duration of an active text/voice conversation but returns on debrief/completed —
  // mirrors HomeButton.jsx's observer exactly. documentElement is stable for the app
  // lifetime, so one observer suffices; read once up front in case ChatPage mounted
  // before us.
  useEffect(() => {
    const el = document.documentElement;
    const read = () => setChatPhase(el.getAttribute(CHAT_PHASE_ATTR));
    read();
    const obs = new MutationObserver(read);
    obs.observe(el, { attributes: true, attributeFilter: [CHAT_PHASE_ATTR] });
    return () => obs.disconnect();
  }, []);

  // Task 13 (self-serve-analytics epic): role+flag check now lives in the shared
  // isAnalyticsAskEligible helper so this FAB and the /org-dashboard "Insights" nav
  // entry (OrgDashboardInsightsNav.jsx) read one definition and can't drift. It folds
  // both conditions into a single boolean, so we pass it through isAskPraxisFabVisible's
  // `roleOk` param and hold `enabled` at true — `roleOk && enabled` then reduces to
  // exactly `eligible`, preserving isAskPraxisFabVisible's own behaviour byte-for-byte
  // (its pinned unit tests call it directly with separate roleOk/enabled and are
  // untouched by this refactor).
  const eligible = isAnalyticsAskEligible(user);
  // /insights hosts the full Ask surface inline — a floating "Ask" button on top of the page
  // that IS the ask experience is redundant clutter (human-test 2026-07-04).
  const onInsightsPage = location.pathname === '/insights';
  // Reuse the app's existing public-route definition — do NOT reinvent it (Codex P2.4).
  // #1713: hide during an active text/voice chat (CHAT_PHASE.ACTIVE) so the FAB doesn't
  // compete with the session UI — see askPraxisFabVisibility.js for why chatPhase (not a
  // route match) is the signal.
  // #2079: hide on mobile everywhere except /dashboard and /org-dashboard
  // (mega77 human-test, staging 00907-6p5 session 138 — the FABs "really get
  // in the way" on mobile off those two screens). Desktop is unaffected —
  // see lib/fabMobileVisibility.js's docstring for why this lives here
  // (a visibility gate) rather than in fabStack.js (a stacking-only gate
  // that would also suppress the unrelated CoachBubble debrief FAB).
  const mobileAllowed = isFabAllowedOnMobile({ isMobile, pathname: location.pathname });

  const visible = isAskPraxisFabVisible({
    isAuthenticated,
    roleOk: eligible,
    enabled: true,
    onInsightsPage,
    isPublic: isPublicRoute(location.pathname),
    chatPhase,
    mobileAllowed,
  });

  // review-fix H_fabstack_1696 (P1): memoized so this identity is stable
  // across re-renders that don't change what tapping the item should do
  // (mobile-viewport listener, chat-phase MutationObserver). See
  // BugReporter.jsx's handleTriggerActivate for why: FabStackContext's
  // useFabStackSlot re-runs register() on every render while active, relying
  // on register()'s shallow-equality bail-out to skip a real state update
  // when nothing changed — an unmemoized inline arrow here would get a new
  // reference on every render, defeat that bail-out, and loop renders.
  const handleTriggerActivate = useCallback(() => setOpen(true), []);

  // #1696: claim a slot in the shared 'menu'-corner FabStack whenever we're
  // eligible (position + render descriptor only — visibility stays entirely
  // owned by `visible` above). FabMenu renders the actual button and calls
  // back into `onClick` on tap.
  useFabStackSlot('menu', 'askPraxis', FAB_PRIORITY.ASK_PRAXIS, visible, {
    icon: ASK_PRAXIS_ICON,
    label: `Ask ${appName}`,
    ariaLabel: `Ask ${appName}`,
    onClick: handleTriggerActivate,
    testId: 'fab-menu-item-ask-praxis',
  });

  // If the FAB becomes hidden (logout, role/flag change, public route) while the drawer is
  // open, force it closed — otherwise the scroll-lock effect below would leave the body stuck
  // at overflow:hidden with no visible drawer (Codex code-review P1).
  useEffect(() => { if (!visible) setOpen(false); }, [visible]);

  // Lock body scroll while the drawer is open (Gemini P2.6). Setting open=false (above) when
  // not visible makes this effect re-run and clean up. Hooks run unconditionally — before the
  // early return below.
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!visible) return null;

  function handleGenerateReport() {
    setOpen(false);
    navigate('/learner-progress?view=reports', { state: { openReportBuilder: true } });
  }

  return (
    <>
      {open && (
        <div className="aa-drawer-scrim" onClick={() => setOpen(false)}>
          <aside className="aa-drawer" role="dialog" aria-label={`Ask ${appName}`} onClick={(e) => e.stopPropagation()}>
            <header className="aa-drawer__head">
              <div className="aa-drawer__title">Ask {appName}</div>
              <button type="button" className="aa-drawer__x" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </header>
            <div className="aa-drawer__body">
              <AnalyticsAsk onGenerateReport={handleGenerateReport} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
